import { lighten } from "polished";
import React from "react";
import styled from "styled-components";
import { APIRCodeProject, APIRCodeSearchResult } from "../../api/types/projects";
import Button from "../../components/Button";
import { Header, SubHeader } from "../../components/Header";
import Loading from "../../components/Loading";
import ProjectCard from "../../components/ProjectCard";
import ProjectMinimal from "../../store/models/ProjectMinimal";

const Container = styled.div`
  margin-top: 1.875rem;
  border-bottom: 2px solid rgba(0, 0, 0, 0.1);
  padding-bottom: 0.625rem;
`;

const SearchLoading = styled.div`
  min-height: 4rem;

  .container {
    padding: 1rem 0;
  }
`;

const Content = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  margin-bottom: 1.25rem;
  flex-wrap: wrap;
  gap: 1.25rem;
`;

const ProjectGrid = styled.div`
  display: grid;
  gap: 1.25rem;
  grid-template-columns: repeat(auto-fill, 260px);
  width: 100%;
`;

const HeaderContainer = styled.div`
  min-width: 141px;
  margin-right: 3.125rem;
`;

const EmptyText = styled.div`
  color: ${lighten(0.5, "#000000")};
  text-align: center;
  user-select: none;
  font-size: 1rem;
  line-height: 1rem;
  font-weight: 600;
  margin: 5rem 0;
`;

const ErrorContainer = styled.div`
  background-color: #fff3cd;
  border: 1px solid #ffc107;
  border-radius: 4px;
  padding: 1rem;
  margin: 1rem 0;
  color: #856404;
  font-size: 0.875rem;
`;

const ResultSection = styled.section`
  margin-bottom: 1.875rem;
`;

const Pagination = styled.div`
  align-items: center;
  display: flex;
  gap: 0.75rem;
  margin-top: 1.25rem;
`;

const PageStatus = styled.span`
  color: #666;
  font-size: 0.875rem;
`;

interface Props {
  rcode: string;
  result: APIRCodeSearchResult | null;
  loading: boolean;
  error: string | null;
  urlPrefix: string;
  // Paging remains in the parent so a project action can refresh the visible result page.
  page: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onProjectSubmit: () => Promise<void>;
  onArchiveRestore: () => Promise<void>;
  folderArchived?: boolean;
}

/**
 * Component to display R-Code search results with all associated projects.
 * Groups active and archived projects like the regular Projects Drive.
 * Uses the standard ProjectCard to keep the Drive presentation consistent.
 */
const RCodeSearchResults: React.FC<Props> = ({
  rcode,
  result,
  loading,
  error,
  urlPrefix,
  page,
  pageSize,
  onPageChange,
  onProjectSubmit,
  onArchiveRestore,
  folderArchived = false,
}) => {
  // A partial API response should render an empty state instead of crashing the Drive.
  const projects = result?.projects ?? [];
  // An archived folder uses the same muted card treatment as its standard project view.
  const activeProjects = folderArchived ? [] : projects.filter((project) => project.isActive);
  const archivedProjects = folderArchived
    ? projects
    : projects.filter((project) => !project.isActive);

  // Keep the current cards visible while the next page or query is loading.
  if (loading && !result) {
    return (
      <Container>
        <SearchLoading>
          <Loading size="sm" />
        </SearchLoading>
      </Container>
    );
  }

  if (error) {
    return (
      <Container>
        <ErrorContainer>
          <strong>Search Error:</strong> {error}
        </ErrorContainer>
      </Container>
    );
  }

  if (!result || projects.length === 0) {
    return (
      <Container>
        <Content>
          <HeaderContainer>
            <SubHeader>R-Code Search</SubHeader>
            <Header>Results for "{rcode}"</Header>
          </HeaderContainer>
        </Content>
        <EmptyText>
          No projects found using R-Code <strong>{rcode}</strong>
        </EmptyText>
      </Container>
    );
  }

  return (
    <Container>
      {projects.length > 0 && (
        <>
          {activeProjects.length > 0 && (
            <ResultSection>
              <SubHeader>Projects</SubHeader>
              <Header>{activeProjects.length} Active Projects</Header>
              <ProjectGrid>
                {activeProjects.map((project: APIRCodeProject) => (
                  <ProjectCard
                    key={project.id}
                    // R-Code results expose the same read-only project fields as the Drive card.
                    project={project as unknown as ProjectMinimal}
                    urlPrefix={urlPrefix}
                    folderName={project.folderName || undefined}
                    onProjectSubmit={onProjectSubmit}
                    onArchiveRestore={onArchiveRestore}
                  />
                ))}
              </ProjectGrid>
            </ResultSection>
          )}
          {archivedProjects.length > 0 && (
            <ResultSection>
              <SubHeader>Projects</SubHeader>
              <Header>{archivedProjects.length} Archived Projects</Header>
              <ProjectGrid>
                {archivedProjects.map((project: APIRCodeProject) => (
                  <ProjectCard
                    key={project.id}
                    // Archived results use the card's existing muted archived treatment.
                    project={project as unknown as ProjectMinimal}
                    archived
                    urlPrefix={urlPrefix}
                    folderName={project.folderName || undefined}
                    onProjectSubmit={onProjectSubmit}
                    onArchiveRestore={onArchiveRestore}
                  />
                ))}
              </ProjectGrid>
            </ResultSection>
          )}
          <Pagination>
            <Button
              disabled={page === 1}
              onClick={() => onPageChange(page - 1)}
            >
              Previous
            </Button>
            <PageStatus>
              Page {page} · {projects.length} projects
            </PageStatus>
            <Button
              disabled={!result.hasNext}
              onClick={() => onPageChange(page + 1)}
            >
              Next
            </Button>
          </Pagination>
        </>
      )}
    </Container>
  );
};

export default RCodeSearchResults;
